/**
 * ODDS DataSource
 * @module
 */
var _ = require('optimizely-client-fn');

var AsyncRequest = require('core/utils/async_request');
var createError = require('core/utils/custom_error').create;
var JSONEnv = require('core/lib/json');
var PromiseEnv = require('core/lib/promise').Promise;
var Query = require('core/utils/query');
var XHR = require('core/utils/xhr');

var ListTargetingError = exports.Error = createError('ListTargetingError');

/**
 * @typedef {Object} ListTargetingData
 * @property {Object.<boolean>} values Map of list to list value, for defined lists
 * @property {Object.<string>} metadata Map of list locator key name to value
 */

/**
 * Returns a deferred resolved a key-value map
 * @param {string} projectId
 * @param {Object.<string>} locatorData
 * @return {Promise} Fulfills with {@link module:feature_list/sources/list_targeting~ListTargetingData}, or rejects if cannot get list targeting result
 */
exports.fetchListMemberships = function(projectId:string, locatorData:Record<string, string>): Promise<any> {
  var oddsURL = getODDSUrl(projectId, locatorData);
  if (!oddsURL) {
    return PromiseEnv.reject(new ListTargetingError('Unknown list targeting endpoint'));
  }
  return AsyncRequest.makeAsyncRequest(oddsURL, _.partial(fetchODDS, oddsURL)).then(function(data) {
    if (__INVARIANT__) {
      require('core/utils/invariant').isType('ListData', data);
    }

    return {
      values: data.lists,
      // store a copy of the input data for this request (used later for cache invalidation)
      metadata: locatorData,
    };
  }, function(xhr) {
    throw new ListTargetingError('Error getting data: ' + (xhr.statusText || 'Could not contact server'));
  });
};

/**
 * See https://confluence.sso.episerver.net/display/EXPENG/ODDS+Targeting+Endpoints
 *
 * Exported to facilitate stubbing.
 *
 * @private
 */
exports.ODDS_URL = __LIST_TARGETING_ENDPOINT__;

function fetchODDS(url) {
  return XHR.request({url: url})
    .then(function(xhr) {
      AsyncRequest.resolveRequest(url, JSONEnv.parse(xhr.responseText));
    })
    ['catch'](_.partial(AsyncRequest.rejectRequest, url));
}

function getODDSUrl(projectId, locatorData) {
  // Sanity check. A snippet built with __TARGETING_CONDITIONS__.LIST (and therefore src/plugins/feature_list/)
  // should always include an appropriate __LIST_TARGETING_ENDPOINT__.
  if (_.isNull(exports.ODDS_URL)) {
    return null;
  }

  var params = {
    project: projectId,
  };

  _.each(locatorData, function(locatorValue, locatorName) {
    // locatorValue is guaranteed encoded but locatorName is not
    params[encodeURIComponent(locatorName)] = locatorValue;
  });

  // Generate URL
  return exports.ODDS_URL + '?' + Query.queryStringFromMap(params);
}

if (__TEST__) {
  exports.getODDSUrl = getODDSUrl;
}
